"use client";

import { useState, type ReactNode } from "react";
import { savePages } from "../actions";
import type { Pages, PagesText } from "../types";
import { LanguageBar, langProps, type EditLang } from "./locale";
import { SaveBar, useSave } from "./SaveBar";
import { BTN_GHOST, CARD, IconButton, INPUT, LinesEditor, PageTitle, TextArea, TextField } from "./ui";

type Home = Pages["home"];
type TextPart = "home" | "about" | "contact" | "settings";

/** Page text (Home, About, Contact) or the site settings, edited in place in English and Arabic. Empty fields fall back
 * to the built-in text of that language. */
export function PagesEditor({ pages: initial, part, top }: { pages: Pages; part: "pages" | "settings"; top?: ReactNode }) {
  const [pages, setPages] = useState(initial);
  const [lang, setLang] = useState<EditLang>("en");
  const { pending, notice, dirty, markDirty, run } = useSave();
  // The text of the language being edited: English is the top level, Arabic is under `ar`.
  const text: PagesText = lang === "en" ? pages : pages.ar;
  const setText = <P extends TextPart, K extends keyof PagesText[P]>(partName: P, field: K, value: PagesText[P][K]) => {
    markDirty();
    setPages((p) =>
      lang === "en"
        ? { ...p, [partName]: { ...p[partName], [field]: value } }
        : { ...p, ar: { ...p.ar, [partName]: { ...p.ar[partName], [field]: value } } },
    );
  };
  const setHome = <K extends keyof Home>(field: K, value: Home[K]) => setText("home", field, value);
  /** Addresses and email: the same in both languages. */
  const setShared = <K extends keyof Pages["settings"]>(field: K, value: Pages["settings"][K]) => {
    markDirty();
    setPages((p) => ({ ...p, settings: { ...p.settings, [field]: value } }));
  };
  const h = text.home;
  const s = pages.settings;
  const l = langProps(lang);

  return (
    <div>
      <PageTitle title={part === "pages" ? "Page text" : "Settings"} />
      {top && <div className="mb-8">{top}</div>}
      <LanguageBar
        lang={lang}
        onChange={setLang}
        arabicNote={part === "pages" ? "Arabic text for the /ar pages. Leave a field empty to use the built-in Arabic text." : "Arabic versions of the settings that are words. Email and Instagram are shared: change them on the English tab."}
      />
      {part === "pages" ? (
        <div key={lang} className="flex flex-col gap-6">
          <p className="text-midnight/70">Leave a field empty to use the built-in text. Headings: one line per row of the big heading.</p>
          <section className={`${CARD} flex flex-col gap-5`}>
            <h2 className="text-xl font-bold">Home: hero</h2>
            <TextField lang={lang} label="Small line above the headline" value={h.heroEyebrow} onChange={(v) => setHome("heroEyebrow", v)} />
            <LinesEditor lang={lang} label="Headline" value={h.heroTitle} onChange={(v) => setHome("heroTitle", v)} />
            <TextField lang={lang} label="Line under the headline" value={h.heroSub} onChange={(v) => setHome("heroSub", v)} />
          </section>
          <section className={`${CARD} flex flex-col gap-5`}>
            <h2 className="text-xl font-bold">Home: story</h2>
            <TextField lang={lang} label="Day panel: small line" value={h.dayEyebrow} onChange={(v) => setHome("dayEyebrow", v)} />
            <LinesEditor lang={lang} label="Day panel: heading" value={h.dayTitle} onChange={(v) => setHome("dayTitle", v)} />
            <TextField lang={lang} label="Day panel: line under the heading" value={h.daySub} onChange={(v) => setHome("daySub", v)} />
            <TextArea lang={lang} label="Day panel: paragraph" value={h.dayBody} onChange={(v) => setHome("dayBody", v)} rows={4} />
            <TextField lang={lang} label="Night panel: small line" value={h.nightEyebrow} onChange={(v) => setHome("nightEyebrow", v)} />
            <LinesEditor lang={lang} label="Night panel: heading" value={h.nightTitle} onChange={(v) => setHome("nightTitle", v)} />
            <TextField lang={lang} label="Night panel: line under the heading" value={h.nightSub} onChange={(v) => setHome("nightSub", v)} />
            <LinesEditor lang={lang} label="Services section heading" value={h.servicesTitle} onChange={(v) => setHome("servicesTitle", v)} />
          </section>
          <section className={`${CARD} flex flex-col gap-5`}>
            <h2 className="text-xl font-bold">Home: promises</h2>
            <TextField lang={lang} label="Heading" value={h.promisesTitle} onChange={(v) => setHome("promisesTitle", v)} />
            {h.promises.map((p, i) => (
              <div key={i} className="flex flex-col gap-2 rounded-xl border border-midnight/10 bg-offwhite p-3">
                <div className="flex items-center gap-2">
                  <input aria-label={`Promise ${i + 1} title`} value={p.title} onChange={(e) => setHome("promises", h.promises.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} placeholder="Title" className={`${INPUT} font-bold`} {...l} />
                  <IconButton label="Remove promise" onClick={() => setHome("promises", h.promises.filter((_, j) => j !== i))}>✕</IconButton>
                </div>
                <textarea aria-label={`Promise ${i + 1} text`} rows={2} value={p.body} onChange={(e) => setHome("promises", h.promises.map((x, j) => (j === i ? { ...x, body: e.target.value } : x)))} placeholder="Text" className={`${INPUT} resize-y`} {...l} />
              </div>
            ))}
            <button type="button" onClick={() => setHome("promises", [...h.promises, { title: "", body: "" }])} className={`${BTN_GHOST} self-start`}>
              + Add promise
            </button>
            <TextField lang={lang} label="Last card: title" value={h.reservedTitle} onChange={(v) => setHome("reservedTitle", v)} />
            <TextField lang={lang} label="Last card: button text" value={h.reservedCta} onChange={(v) => setHome("reservedCta", v)} />
          </section>
          <section className={`${CARD} flex flex-col gap-5`}>
            <h2 className="text-xl font-bold">About page</h2>
            <LinesEditor lang={lang} label="Big moving words" value={text.about.rows} onChange={(v) => setText("about", "rows", v)} />
            <TextArea lang={lang} label="Statement" value={text.about.body} onChange={(v) => setText("about", "body", v)} rows={4} />
            <LinesEditor lang={lang} label="Fact cards" value={text.about.facts} onChange={(v) => setText("about", "facts", v)} addLabel="Add fact" />
          </section>
          <section className={`${CARD} flex flex-col gap-5`}>
            <h2 className="text-xl font-bold">Contact page</h2>
            <LinesEditor lang={lang} label="Heading" value={text.contact.title} onChange={(v) => setText("contact", "title", v)} />
          </section>
        </div>
      ) : (
        <section key={lang} className={`${CARD} flex flex-col gap-5`}>
          {lang === "en" && (
            <>
              <TextField label="Contact email" type="email" value={s.email} onChange={(v) => setShared("email", v)} hint="Shown on the Contact page in both languages. Leave empty to show “Coming soon”." />
              <TextField label="Instagram address" value={s.instagram} onChange={(v) => setShared("instagram", v)} placeholder="https://www.instagram.com/…" />
              <TextField label="Instagram handle" value={s.instagramHandle} onChange={(v) => setShared("instagramHandle", v)} placeholder="@tasma.eg" />
            </>
          )}
          <TextField lang={lang} label="Where (shown on the Contact page)" value={text.settings.location} onChange={(v) => setText("settings", "location", v)} />
          <TextArea lang={lang} label="Footer line under the logo" value={text.settings.footerTagline} onChange={(v) => setText("settings", "footerTagline", v)} rows={2} />
          <TextArea lang={lang} label="Contact panel intro" value={text.settings.contactIntro} onChange={(v) => setText("settings", "contactIntro", v)} rows={3} />
          <LinesEditor
            lang={lang}
            label="Budget options in the contact form"
            value={text.settings.budgets}
            onChange={(v) => setText("settings", "budgets", v)}
            addLabel="Add option"
            hint="Keep the same options, in the same order, in English and Arabic: the statistics count them together."
          />
        </section>
      )}
      <SaveBar onSave={() => run(() => savePages(pages))} pending={pending} dirty={dirty} notice={notice} />
    </div>
  );
}
